import type { B2BBrandingConfig, ResolvedBranding } from "./types";

export const DEFAULT_APP_NAME = "Aksademia";

export const DEFAULT_BRANDING: Readonly<ResolvedBranding> = {
  appName: DEFAULT_APP_NAME,
};

/**
 * Applies defaults to a tenant branding config (or null). Pure — always returns a
 * fresh object so callers can never mutate the shared default. A blank/missing
 * appName falls back to the Aksademia default so the UI never renders an empty name.
 */
export function resolveBranding(
  config: B2BBrandingConfig | null,
): ResolvedBranding {
  if (!config) return { ...DEFAULT_BRANDING };

  const appName = config.appName?.trim() || DEFAULT_APP_NAME;
  const resolved: ResolvedBranding = { appName };

  if (config.logoUrl) resolved.logoUrl = config.logoUrl;
  if (config.logoDarkUrl) resolved.logoDarkUrl = config.logoDarkUrl;
  if (config.faviconUrl) resolved.faviconUrl = config.faviconUrl;
  if (config.supportEmail) resolved.supportEmail = config.supportEmail;
  if (config.legalEntityName) resolved.legalEntityName = config.legalEntityName;
  if (config.primaryColor) resolved.primaryColor = config.primaryColor;
  if (config.accentColor) resolved.accentColor = config.accentColor;

  return resolved;
}

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Drops any brand colour that isn't a plain hex value. Layouts put these straight into a
 * React style object as CSS custom properties, so a value like "red;background:url(//x)"
 * would inject extra declarations. Applied to branding that doesn't come from a signed
 * lease (a white-label instance's own /v1/instance/runtime projection).
 */
export function sanitizeBrandingColors<T extends B2BBrandingConfig | null>(config: T): T {
  if (!config) return config;
  const safe = { ...config };
  if (safe.primaryColor !== undefined && !HEX_COLOR.test(safe.primaryColor)) delete safe.primaryColor;
  if (safe.accentColor !== undefined && !HEX_COLOR.test(safe.accentColor)) delete safe.accentColor;
  return safe;
}
